import { useMemo, useState } from 'react';
import { SegmentGroup } from 'semantic-ui-react';
import { FiltersBar, SuiTable, SuiTableState } from '@britishcouncil/react-admin';
import { usePermissions } from '@britishcouncil/react-common';

import { OnlineSpeakingMonitoringDto, OnlineSpeakingMonitoringStatus, Permission } from 'api';
import { useExaminerId } from 'components';
import {
  useOnlineSpeakingMonitoringListQuery,
  useUpdateOnlineSpeakingMonitoringFollowUpMutation,
  useBulkDeleteOnlineSpeakingMonitoringMutation,
} from 'store';
import { columns } from './OnlineSpeakingMonitoringList.columns';
import {
  filterMonitorings,
  FILTERS_DEFINITION,
  OnlineSpeakingMonitoringFiltersValues,
} from './OnlineSpeakingMonitoringList.filters';
import { MonitoringTableProvider, MonitoringTableActions } from '../common';

const INITIAL_FILTERS_STATE: OnlineSpeakingMonitoringFiltersValues = {
  status: [OnlineSpeakingMonitoringStatus.Current],
  date: undefined,
};

const INITIAL_TABLE_STATE: SuiTableState<OnlineSpeakingMonitoringDto> = {
  pageIndex: 0,
  pageSize: 10,
  sortBy: [{ id: 'monitoringDate', desc: true }],
};

export const OnlineSpeakingMonitoringList = () => {
  const examinerId = useExaminerId();
  const { permissions } = usePermissions();
  const [filtersState, setFiltersState] =
    useState<OnlineSpeakingMonitoringFiltersValues>(INITIAL_FILTERS_STATE);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const { data, isFetching } = useOnlineSpeakingMonitoringListQuery({ examinerId });
  const [updateFollowUp] = useUpdateOnlineSpeakingMonitoringFollowUpMutation();
  const [bulkDelete] = useBulkDeleteOnlineSpeakingMonitoringMutation();

  const filteredData = useMemo(() => filterMonitorings(data, filtersState), [data, filtersState]);

  const hasCurrentMonitorings = useMemo(
    () => filteredData.some((m) => m.status === OnlineSpeakingMonitoringStatus.Current),
    [filteredData]
  );

  const handleDelete = async (selectedIds: Set<number>) => {
    await bulkDelete({ ids: Array.from(selectedIds) }).unwrap();
    setSelectedIds(new Set());
  };

  const showAddButton = permissions.includes(Permission.UpdateMonitoringResults);
  const showDeleteButton = permissions.includes(Permission.DeleteAndRestoreMonitoringResults);

  return (
    <MonitoringTableProvider
      onUpdateFollowUp={updateFollowUp}
      selectedIds={selectedIds}
      onSelectionChange={setSelectedIds}
    >
      <SegmentGroup>
        <FiltersBar
          value={filtersState}
          filters={FILTERS_DEFINITION}
          clearable={false}
          onFiltersChange={setFiltersState}
          attached
        />
        <MonitoringTableActions
          onDelete={handleDelete}
          archiveDisabled={!hasCurrentMonitorings}
          hasAddButton={showAddButton}
          hasDeleteButton={showDeleteButton}
        />
        <SuiTable
          disableFilters
          disableSortBy={false}
          data={filteredData}
          columns={columns}
          loading={isFetching}
          initialState={INITIAL_TABLE_STATE}
          tableProps={{
            selectable: true,
            sortable: true,
            'data-testid': 'monitoring-list-table',
          }}
          tableRowProps={(_, { row }) => ({
            negative: row.original.status === OnlineSpeakingMonitoringStatus.Deleted,
            warning: row.original.status === OnlineSpeakingMonitoringStatus.Archived,
            className:
              row.original.status === OnlineSpeakingMonitoringStatus.Deleted
                ? 'deleted-record'
                : '',
          })}
        />
      </SegmentGroup>
    </MonitoringTableProvider>
  );
};
